import ShortUniqueId from "short-unique-id";
import { compose, createBlock, createComponentBlock, createExternalLink, createImageBlock, createItemLink, createListBlock, createMark, createSpan, createTable, createTableCell, createTableRow, findLastIndex, isElement, isExternalLink, isListBlock, isListItem, isOrderedListBlock, isText, isUnorderedListBlock } from "../../utils";
const blockElements = ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'];
const markElements = ['strong', 'em', 'sub', 'sup', 'code'];
const ignoredElements = ['img', 'tbody', 'ol', 'ul'];
const uid = new ShortUniqueId({ length: 16 });
export const transformToPortableText = (parsedTree) => {
    const flattened = flatten(parsedTree.children, 0);
    return composeAndMerge(flattened);
};
const mergeSpansAndMarks = (itemsToMerge) => {
    let marks = [];
    let links = [];
    let linkChildCount = 0;
    const mergedItems = itemsToMerge.reduce((mergedItems, item) => {
        switch (item._type) {
            case 'internalLink':
            case 'link': {
                const lastBlockIndex = findLastIndex(mergedItems, item => item._type === 'block');
                const updatedBlock = mergedItems[lastBlockIndex];
                if ('markDefs' in updatedBlock) {
                    updatedBlock.markDefs.push(item);
                    mergedItems[lastBlockIndex] = updatedBlock;
                }
                else {
                    const newBlock = createBlock(uid().toString());
                    newBlock.markDefs.push(item);
                    mergedItems.push(newBlock);
                }
                break;
            }
            case 'mark':
                marks.push(item.value);
                break;
            case 'linkMark':
                links.push(item.value);
                linkChildCount = item.childCount;
                break;
            case 'span':
                if (linkChildCount === 0) {
                    links = [];
                }
                else {
                    linkChildCount--;
                }
                item.marks = [...marks, ...links];
                mergedItems.push(item);
                marks = [];
                break;
            default:
                links = [];
                mergedItems.push(item);
                break;
        }
        return mergedItems;
    }, []);
    return mergedItems;
};
const mergeBlocksAndSpans = (itemsToMerge) => {
    const mergedItems = itemsToMerge.reduce((mergedItems, item) => {
        if (item._type === 'span') {
            const previousBlock = mergedItems.pop();
            previousBlock.children.push(item);
            mergedItems.push(previousBlock);
        }
        else {
            mergedItems.push(item);
        }
        return mergedItems;
    }, []);
    return mergedItems;
};
const mergeTablesAndRows = (itemsToMerge) => {
    const mergedItems = itemsToMerge.reduce((mergedItems, item) => {
        if (item._type === 'row') {
            const tableBlock = mergedItems.pop();
            tableBlock.rows.push(item);
            mergedItems.push(tableBlock);
        }
        else {
            mergedItems.push(item);
        }
        return mergedItems;
    }, []);
    return mergedItems;
};
const mergeRowsAndCells = (itemsToMerge) => {
    const mergedItems = itemsToMerge.reduce((mergedItems, item) => {
        if (item._type === 'cell') {
            const tableRow = mergedItems.pop();
            tableRow.cells.push(item);
            mergedItems.push(tableRow);
        }
        else {
            mergedItems.push(item);
        }
        return mergedItems;
    }, []);
    return mergedItems;
};
const composeAndMerge = compose(mergeTablesAndRows, mergeRowsAndCells, mergeBlocksAndSpans, mergeSpansAndMarks);
const flatten = (nodes, depth = 0, lastListElement, listType) => {
    return nodes.flatMap((node) => {
        let children = [];
        let transformedChildren = [];
        let currentListType = listType;
        const finishedItems = [];
        if (isElement(node)) {
            children = node.tagName === 'td' ? [] : node.children;
            if (isUnorderedListBlock(node)) {
                lastListElement = node;
                currentListType = 'bullet';
            }
            else if (isOrderedListBlock(node)) {
                lastListElement = node;
                currentListType = 'number';
            }
            else if (isListItem(node)) {
                if (lastListElement && isListBlock(lastListElement))
                    depth = depth + 1;
                lastListElement = node;
            }
        }
        const transformedNode = transformNode(node, depth, currentListType);
        transformedChildren = flatten(children, depth, lastListElement, currentListType);
        finishedItems.push(...transformedNode, ...transformedChildren);
        return finishedItems;
    }, []);
};
const transformNode = (node, depth, listType) => {
    if (isText(node)) {
        return [transformText(node)];
    }
    else {
        return transformElement(node, depth, listType);
    }
};
const transformElement = (node, depth, listType) => {
    const transformerFunction = transformMap[node.tagName];
    return transformerFunction(node, depth, listType);
};
const transformImage = (node) => {
    const block = createImageBlock(uid().toString());
    const imageTag = node.children[0];
    block.asset._ref = node.attributes['data-image-id'];
    block.asset.url = imageTag.attributes['src'];
    return [block];
};
const transformTableCell = (node) => {
    const cellContent = flatten(node.children, 0);
    const isFirstChildText = (node.children[0]?.type === 'text' ||
        ['br', 'a', 'strong', 'em', 'sup', 'sub', 'code'].includes(node.children[0]?.tagName));
    if (isFirstChildText)
        cellContent.unshift(createBlock(uid().toString()));
    const mergedCellContent = composeAndMerge(cellContent);
    const tableCell = createTableCell(uid().toString(), mergedCellContent.length);
    tableCell.content = mergedCellContent;
    return [tableCell];
};
const transformItem = (node) => {
    const itemReference = {
        _type: 'reference',
        _ref: node.attributes['data-codename']
    };
    return [createComponentBlock(uid().toString(), itemReference)];
};
const transformLink = (node) => {
    if (isExternalLink(node)) {
        return transformExternalLink(node);
    }
    else {
        return transformInternalLink(node);
    }
};
const transformInternalLink = (node) => {
    const link = createItemLink(uid().toString(), node.attributes['data-item-id']);
    const mark = createMark(uid().toString(), link._key, 'linkMark', node.children.length);
    return [link, mark];
};
const transformExternalLink = (node) => {
    const link = createExternalLink(uid().toString(), node.attributes);
    const mark = createMark(uid().toString(), link._key, "linkMark", node.children.length);
    return [link, mark];
};
const transformTable = (node) => {
    const tableBody = node.children[0];
    const tableRow = tableBody.children[0];
    const numCols = tableRow.children.length;
    return [createTable(uid().toString(), numCols)];
};
const transformTableRow = () => [createTableRow(uid().toString())];
const transformText = (node) => createSpan(uid().toString(), [], node.content);
const transformBlock = (node) => [createBlock(uid().toString(), undefined, node.tagName === 'p' ? 'normal' : node.tagName)];
const transformTextMark = (node) => [createMark(uid().toString(), node.tagName, 'mark', node.children.length)];
const transformLineBreak = () => [createSpan(uid().toString(), [], '\n')];
const transformListItem = (_, depth, listType) => [createListBlock(uid().toString(), depth, listType)];
const ignoreElement = () => [];
const transformMap = {
    ...Object.fromEntries(blockElements.map(tagName => [tagName, transformBlock])),
    ...Object.fromEntries(markElements.map(tagName => [tagName, transformTextMark])),
    ...Object.fromEntries(ignoredElements.map(tagName => [tagName, ignoreElement])),
    'a': transformLink,
    'li': transformListItem,
    'table': transformTable,
    'tr': transformTableRow,
    'td': transformTableCell,
    'br': transformLineBreak,
    'figure': transformImage,
    'object': transformItem,
    default: (node) => {
        throw new Error(`No transformer implemented for tag "${node.tagName}"`);
    }
};
